import { useState } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { User, Lock, Ticket } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  InputOTPSeparator,
} from '@/components/ui/input-otp'
import { useAuthStore } from '@/features/auth/store/auth-store'
import { usePhone } from '../../hooks/use-phone'
import { PhoneVerifyInput } from '../shared/phone-verify-input'

const registerSchema = z.object({
  otp: z.string().min(6, '请输入6位验证码').max(6, '请输入6位验证码'),
  username: z
    .string()
    .min(3, '用户名至少3个字符')
    .max(50, '用户名最多50个字符'),
  password: z.string().min(6, '密码至少6个字符'),
  inviteCode: z.string().optional(),
})

type RegisterPhoneFormProps = React.HTMLAttributes<HTMLDivElement>

export function RegisterPhoneForm({
  className,
  ...props
}: RegisterPhoneFormProps) {
  const { authRegisterPhoneMutationAsync } = usePhone()
  const { handlePhoneRegister } = useAuthStore((s) => s.handlers)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [isCodeSent, setIsCodeSent] = useState(false)

  const form = useForm<z.infer<typeof registerSchema>>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      otp: '',
      username: '',
      password: '',
      inviteCode: '',
    },
  })

  async function onSubmit(data: z.infer<typeof registerSchema>) {
    await handlePhoneRegister(
      {
        phone: phoneNumber,
        code: data.otp,
        username: data.username,
        password: data.password,
        inviteCode: data.inviteCode,
      },
      authRegisterPhoneMutationAsync
    )
  }

  return (
    <div className={cn('grid gap-4', className)} {...props}>
      {!isCodeSent ? (
        <PhoneVerifyInput
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          onCodeSent={() => setIsCodeSent(true)}
          type='register'
        />
      ) : (
        <div className='flex flex-col gap-4'>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className='grid gap-4'>
              <FormField
                control={form.control}
                name='otp'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='sr-only'>One-Time Password</FormLabel>
                    <FormControl>
                      <InputOTP
                        maxLength={6}
                        {...field}
                        containerClassName='justify-between sm:[&>[data-slot="input-otp-group"]>div]:w-12'
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                        </InputOTPGroup>
                        <InputOTPSeparator />
                        <InputOTPGroup>
                          <InputOTPSlot index={2} />
                          <InputOTPSlot index={3} />
                        </InputOTPGroup>
                        <InputOTPSeparator />
                        <InputOTPGroup>
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='username'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='sr-only'>用户名</FormLabel>
                    <FormControl>
                      <div className='relative'>
                        <User className='text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2' />
                        <Input
                          {...field}
                          className='bg-muted focus-visible:ring-primary h-12 border-none pl-10 focus-visible:ring-1'
                          placeholder='用户名 (至少3位)'
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='password'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='sr-only'>密码</FormLabel>
                    <FormControl>
                      <div className='relative'>
                        <Lock className='text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2' />
                        <Input
                          {...field}
                          type='password'
                          className='bg-muted focus-visible:ring-primary h-12 border-none pl-10 focus-visible:ring-1'
                          placeholder='密码 (至少6位)'
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='inviteCode'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='sr-only'>邀请码</FormLabel>
                    <FormControl>
                      <div className='relative'>
                        <Ticket className='text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2' />
                        <Input
                          {...field}
                          className='bg-muted focus-visible:ring-primary h-12 border-none pl-10 focus-visible:ring-1'
                          placeholder='邀请码 (可选)'
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className='mt-2 flex flex-col gap-2'>
                <Button
                  type='submit'
                  className='h-12 w-full text-base font-medium'
                  disabled={form.formState.isSubmitting}
                >
                  注册并登录
                </Button>
                <Button
                  variant='ghost'
                  size='sm'
                  className='text-muted-foreground hover:text-primary mx-auto text-xs font-normal'
                  onClick={() => setIsCodeSent(false)}
                  type='button'
                >
                  返回修改手机号
                </Button>
              </div>
            </form>
          </Form>
        </div>
      )}
    </div>
  )
}
